/**
 * The packing panel's moves (slice 8f, ADR 0041; Q85): what the panel at the wheel does to the bag being packed,
 * before the server checks it at the run's start (`POST /api/runs {bag}`; `@ab/session`'s `bagRefusal` there). The
 * rules a bag keeps are `@ab/session`'s (`bagItem`, `dungeonStack`, `autoBag`, the server's own); this module only
 * moves stacks between the bag and what storage holds, so the panel never offers a bag the server would refuse
 * unless storage changed meanwhile.
 *
 * - **Listed:** the bag's stacks in the order they went in; storage's items a bag takes, in item id order, with what is
 *   left of each beside the bag (an item all in the bag is not listed under Aboard).
 * - **In** (a storage row): a full stack (`min(stackSize.dungeon, held)`), or an existing stack topped up to that;
 *   a new stack in a full bag is refused (`full`).
 * - **A count** (left, right, − and +): one step within `0..min(stackSize.dungeon, held)`; at 0 the row says "×0"
 *   (the stack still listed, not flown). **Out** (a bag row confirmed at 0): the stack back aboard. Confirmed with a
 *   count it does nothing, so no single tap or key loses a stack (the slice 8f fix round).
 * - **Auto:** emptied, then filled as the server would (`autoBag`). **Empty:** nothing.
 * - **Go:** the stacks with a count (`flown`).
 * - **A refusal** (`bad_bag`; `afterRefusal`): the first re-reads storage and says "Check the bag."; a second, of a bag
 *   the panel proposed itself (untouched since), falls back to Auto's choice; a third says the game needs an update and
 *   dims Go until the player opens the panel again (a bundle whose rules disagree with the server's cannot loop).
 * - **The remembered bag:** the last bag flown, per account (`localStorage` `ab.bag.<account id>`, in memory when the
 *   browser refuses storage), pre-fills the next panel where storage still allows (each stack cut to what storage
 *   holds and a bag takes; Auto's choice when nothing of a remembered non-empty bag is left); with none, Auto's. A
 *   stored value that is not a list of stacks with counts of at least 1 is ignored; an item twice is kept once.
 */
import type { StorageStack } from '@ab/api-contract';
import type { BagEntry } from '@ab/game-core';
import { autoBag, bagItem, dungeonStack } from '@ab/session';
import { pack } from 'virtual:ab-content';

const items = pack.items;
/**
 * The test hook's bag size (`window.__abScene.setBagSlots`, in a development build only): the content's six packable
 * items never fill the rules' eight slots, so the browser test of "The bag is full." packs against fewer. Null: the
 * rules'. A production build never reads it (`import.meta.env.DEV` is false there, so the override is dropped).
 */
let slotsForTest: number | null = null;
const slots = (): number => (import.meta.env.DEV ? (slotsForTest ?? pack.rules.dungeon.bagSlots) : pack.rules.dungeon.bagSlots);
/** Sets (or, with null, clears) the panel's bag size for a test (`test-hook.ts` only, development builds only). */
export const setBagSlotsForTest = (n: number | null): void => {
  if (import.meta.env.DEV) slotsForTest = n;
};

/** How many of an item storage holds. */
const heldOf = (storage: readonly StorageStack[], item: string): number => storage.find((s) => s.item === item)?.count ?? 0;
/** The most of an item one bag stack may hold now: its dungeon stack, or what storage holds. */
export const stackLimit = (storage: readonly StorageStack[], item: string): number => Math.min(dungeonStack(items, item), heldOf(storage, item));

/** The bag's slots (`rules.dungeon.bagSlots`). */
export const bagSlots = slots;

/** Storage's items a bag takes, with what is left of each beside the bag (none left: not listed), by item id. */
export const aboard = (storage: readonly StorageStack[], bag: readonly BagEntry[]): StorageStack[] =>
  storage
    .filter((s) => bagItem(items, s.item))
    .map((s) => ({ item: s.item, count: s.count - (bag.find((b) => b.item === s.item)?.count ?? 0) }))
    .filter((s) => s.count > 0)
    .sort((a, b) => (a.item < b.item ? -1 : a.item > b.item ? 1 : 0));

/** The stacks a flight takes: those with a count (a row at "×0" stays behind). */
export const flown = (bag: readonly BagEntry[]): BagEntry[] => bag.filter((b) => b.count >= 1);

/** A storage row confirmed: a full stack in, or the bag's stack topped up; `full` when a new stack has no slot. */
export const moveIn = (bag: readonly BagEntry[], storage: readonly StorageStack[], item: string): BagEntry[] | 'full' => {
  if (!bagItem(items, item)) return [...bag];
  const limit = stackLimit(storage, item);
  if (bag.some((b) => b.item === item)) return bag.map((b) => (b.item === item ? { item, count: Math.max(b.count, limit) } : b));
  if (flown(bag).length >= slots()) return 'full';
  return limit < 1 ? [...bag] : [...bag, { item, count: limit }];
};

/** A bag row confirmed: at "×0" (a count of 0) the stack goes back aboard; with a count, nothing changes. */
export const confirmBagRow = (bag: readonly BagEntry[], item: string): BagEntry[] => bag.filter((b) => b.item !== item || b.count >= 1);

/** A bag row's count one step down or up, within `0..min(stackSize.dungeon, held)` (0: "×0"). */
export const adjust = (bag: readonly BagEntry[], storage: readonly StorageStack[], item: string, step: -1 | 1): BagEntry[] => {
  const limit = stackLimit(storage, item);
  return bag.map((b) => (b.item === item ? { item, count: Math.max(0, Math.min(limit, b.count + step)) } : b));
};

/** What the panel does after the server refused its bag (`bad_bag`) for the `refusals`-th time since it was opened. */
export type AfterRefusal = {
  /** The bag the panel opens again with: the refused one cut to storage as read again, or Auto's choice. */
  readonly bag: 'fitted' | 'auto';
  /** Its line. */
  readonly line: 'ui.pack.refused' | 'ui.server.version_mismatch';
  /** Go dimmed until the player opens the panel again. */
  readonly stuck: boolean;
};

/**
 * The refusal's state machine (slice 8f fix round): the first refusal re-reads storage and says "Check the bag."; a
 * second of a bag the panel proposed itself (`proposed`: the player did not touch it since it was offered) falls back to
 * Auto's choice; a third says what the stuck-run path says for a build whose rules disagree with the server's ("The
 * game needs an update.") and dims Go, so a stale bundle never loops on Go.
 */
export const afterRefusal = (refusals: number, proposed: boolean): AfterRefusal => {
  if (refusals >= 3) return { bag: 'fitted', line: 'ui.server.version_mismatch', stuck: true };
  if (refusals === 2 && proposed) return { bag: 'auto', line: 'ui.pack.refused', stuck: false };
  return { bag: 'fitted', line: 'ui.pack.refused', stuck: false };
};

/** Auto: emptied, then filled as the server would. */
export const auto = (storage: readonly StorageStack[]): BagEntry[] => autoBag(items, slots(), storage);

/** A bag cut to what storage holds now (each stack to its limit; an item storage lacks or a bag never takes left out; the slots kept). */
export const fitted = (bag: readonly BagEntry[], storage: readonly StorageStack[]): BagEntry[] => {
  const out: BagEntry[] = [];
  for (const b of bag) {
    if (out.length >= slots() || !bagItem(items, b.item) || out.some((o) => o.item === b.item)) continue;
    const count = Math.min(b.count, stackLimit(storage, b.item));
    if (count >= 1) out.push({ item: b.item, count });
  }
  return out;
};

// ---------------------------------------------------------------- the remembered bag (per account, this device)

const memory = new Map<string, BagEntry[]>();
const keyOf = (account: string): string => `ab.bag.${account}`;

const isStack = (e: unknown): e is BagEntry => {
  if (typeof e !== 'object' || e === null) return false;
  const { item, count } = e as { item?: unknown; count?: unknown };
  return typeof item === 'string' && typeof count === 'number' && Number.isSafeInteger(count) && count >= 1;
};

/** A stored bag read back: a list of stacks each with a count of at least 1 (an item twice kept once), else null. */
export const parseRemembered = (raw: string): BagEntry[] | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const out: BagEntry[] = [];
  for (const e of parsed as unknown[]) {
    if (!isStack(e)) return null;
    if (!out.some((o) => o.item === e.item)) out.push({ item: e.item, count: e.count });
  }
  return out;
};

/** The last bag flown by the account on this device; null when none is known. */
export const rememberedBag = (account: string): BagEntry[] | null => {
  const known = memory.get(account);
  if (known !== undefined) return known;
  try {
    const raw = localStorage.getItem(keyOf(account));
    return raw === null ? null : parseRemembered(raw);
  } catch {
    return null;
  }
};

/** Keeps the bag a flight started with (the server took it). */
export const rememberBag = (account: string, bag: readonly BagEntry[]): void => {
  const copy = bag.map((b) => ({ item: b.item, count: b.count }));
  memory.set(account, copy);
  try {
    localStorage.setItem(keyOf(account), JSON.stringify(copy));
  } catch {
    // Kept in memory only (a private window).
  }
};

/** The bag a new panel starts with: the remembered one where storage still allows it, else Auto's. */
export const startingBag = (account: string | null, storage: readonly StorageStack[]): BagEntry[] => {
  const last = account === null ? null : rememberedBag(account);
  if (last === null) return auto(storage);
  const fit = fitted(last, storage);
  return fit.length === 0 && last.length > 0 ? auto(storage) : fit;
};
